import { useState } from "react";
import { X, CheckCircle2, AlertTriangle, FileText, Camera } from "lucide-react";

interface CompleteTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (data: { notes: string; aftercareShared: boolean }) => void;
  treatment?: {
    id: string;
    clientName: string;
    treatmentName: string;
    practitioner: string;
    price: number;
    paidAmount: number;
    consentSigned: boolean;
  };
}

export default function CompleteTreatmentModal({
  isOpen,
  onClose,
  onComplete,
  treatment,
}: CompleteTreatmentModalProps) {
  const [completionNotes, setCompletionNotes] = useState("");
  const [aftercareShared, setAftercareShared] = useState(false);
  const [confirmChecklist, setConfirmChecklist] = useState({
    treatmentCompleted: false,
    notesAdded: false,
    photosUploaded: false,
  });

  const balance = treatment ? treatment.price - treatment.paidAmount : 0;
  const hasOutstandingBalance = balance > 0;

  const handleComplete = () => {
    onComplete?.({
      notes: completionNotes,
      aftercareShared,
    });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setCompletionNotes("");
    setAftercareShared(false);
    setConfirmChecklist({
      treatmentCompleted: false,
      notesAdded: false,
      photosUploaded: false,
    });
  };

  const canComplete = confirmChecklist.treatmentCompleted;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#e6f7ed] flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#0d7a3e]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#2d2d2d]">Complete Treatment</h2>
              {treatment && (
                <p className="text-sm text-[#6b6b6b]">{treatment.treatmentName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f8f5fc] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5">
          {/* Treatment Summary */}
          {treatment && (
            <div className="p-4 bg-[#f8f5fc] rounded-lg border border-[#e8e0f0]">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Client:</span>
                  <span className="font-medium text-[#2d2d2d]">{treatment.clientName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Practitioner:</span>
                  <span className="font-medium text-[#2d2d2d]">{treatment.practitioner}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Treatment Price:</span>
                  <span className="font-medium text-[#2d2d2d]">£{treatment.price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Amount Paid:</span>
                  <span className="font-medium text-[#2d2d2d]">£{treatment.paidAmount.toFixed(2)}</span>
                </div>
                {hasOutstandingBalance && (
                  <div className="flex justify-between text-sm pt-2 border-t border-[#e8e0f0]">
                    <span className="text-[#e6a700] font-medium">Outstanding Balance:</span>
                    <span className="font-bold text-[#e6a700]">£{balance.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Outstanding Balance Warning */}
          {hasOutstandingBalance && (
            <div className="p-3 bg-[#fff8e6] border border-[#ffd89e] rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#e6a700] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#8a6d00]">Outstanding Balance</p>
                  <p className="text-sm text-[#8a6d00]">
                    This treatment has an outstanding balance of £{balance.toFixed(2)}. 
                    You can still complete the treatment, but payment should be collected.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Consent Warning */}
          {treatment && !treatment.consentSigned && (
            <div className="p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#dc2626] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#dc2626]">Consent Not Signed</p>
                  <p className="text-sm text-[#dc2626]">
                    The consent form has not been signed for this treatment.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Completion Checklist */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-3">
              Completion Checklist
            </label>
            <div className="space-y-2">
              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  confirmChecklist.treatmentCompleted
                    ? "border-[#0d7a3e] bg-[#e6f7ed]"
                    : "border-[#e8e0f0] hover:bg-[#faf8fc]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={confirmChecklist.treatmentCompleted}
                  onChange={(e) =>
                    setConfirmChecklist({ ...confirmChecklist, treatmentCompleted: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-[#e8e0f0] text-[#0d7a3e] focus:ring-[#0d7a3e]"
                />
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0d7a3e]" />
                  <span className="text-sm text-[#3d3d3d]">Treatment has been completed</span>
                </div>
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  confirmChecklist.notesAdded
                    ? "border-[#704aaf] bg-[#f8f5fc]"
                    : "border-[#e8e0f0] hover:bg-[#faf8fc]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={confirmChecklist.notesAdded}
                  onChange={(e) =>
                    setConfirmChecklist({ ...confirmChecklist, notesAdded: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
                />
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#704aaf]" />
                  <span className="text-sm text-[#3d3d3d]">Clinical notes have been added</span>
                </div>
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  confirmChecklist.photosUploaded
                    ? "border-[#704aaf] bg-[#f8f5fc]"
                    : "border-[#e8e0f0] hover:bg-[#faf8fc]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={confirmChecklist.photosUploaded}
                  onChange={(e) =>
                    setConfirmChecklist({ ...confirmChecklist, photosUploaded: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
                />
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-[#704aaf]" />
                  <span className="text-sm text-[#3d3d3d]">After photos uploaded (optional)</span>
                </div>
              </label>
            </div>
          </div>

          {/* Completion Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Completion Notes (Optional)
            </label>
            <textarea
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="Add any final notes about the treatment..."
              className="w-full px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
            />
          </div>

          {/* Share Aftercare */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="share-aftercare"
              checked={aftercareShared}
              onChange={(e) => setAftercareShared(e.target.checked)}
              className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
            />
            <label htmlFor="share-aftercare" className="text-sm text-[#5c5c5c]">
              Share aftercare instructions with client
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#6b6b6b] hover:bg-[#f0ebf5] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleComplete}
              disabled={!canComplete}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0d7a3e] text-white text-sm font-medium rounded-lg hover:bg-[#0a6331] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle2 className="w-4 h-4" />
              Complete Treatment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


